// Dashboard v2 - Self-contained implementation
import React, { useState, useMemo } from 'react';
import { Flame, Wrench, Users, TrendingUp, CircleCheckBig, ChevronRight, ChevronLeft, AlertCircle, X, CalendarDays, ShieldAlert, GraduationCap, Ship, Anchor, Fuel, FileCheck } from 'lucide-react';
import { useVessel } from 'contexts/VesselContext';
import { useDashboardCompliance } from '../../hooks/useDashboardCompliance';
import { useDrillsData } from '../../hooks/domain/useDrillsData';
import { useEquipmentRegister } from '../../hooks/domain/useEquipmentRegister';
import { useCrew } from '../../hooks/useCrew';
import { useDefects } from '../../hooks/domain/defects/useDefects';
import { useUpcomingEvents } from '../../hooks/useUpcomingEvents';
import { useDashboardCategories } from '../../hooks/useDashboardCategories';
import { VesselStatusCard } from './VesselStatusCard';
import { useVoyages } from '../../hooks/domain/operations/useVoyages';
import DashboardDailyChecksCard from './dashboard/DashboardDailyChecksCard';
// FT-324 cycle-3b: DailyChecksQuickAccess reads useChecklistRecordsContext()
// which is only mounted inside OperationsModule. Rendering it here (outside
// the Provider tree) causes a hard crash when the tile is clicked. We now
// redirect to Operations → Checklists instead of opening it inline.

// ============================================
// FT-153: Timezone-safe date string helper
// Prevents UTC conversion that causes off-by-one day errors in UK timezone (GMT+1)
// ============================================
const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ============================================
// INLINE V2 COMPONENTS (self-contained)
// ============================================

// FT-185: Compact Compliance Card Component with Color-coded Progress Bars
// Removed overall score - now shows 4 independent category indicators
// FT-185 follow-up: Cyan bars for 50%+, red only for <50%. Traffic light dots replace info icons.
const CompactComplianceCard = ({ compliance, onCategoryClick }) => {
  const {
    drills,
    equipment,
    crew,
    defects,
    overdueDrillsCount = 0,
    overdueJobsCount = 0,
    expiringCertsCount = 0,
    openDefectsCount = 0
  } = compliance;

  // FT-185 follow-up: Progress bar color - cyan for 50%+, red only below 50%
  const getProgressBarColor = (percentage) => {
    if (percentage >= 50) return 'bg-cyan-600';
    return 'bg-red-500';
  };

  // FT-185 follow-up: Traffic light dot color - green 80-100%, amber 50-79%, red <50%
  const getStatusDotColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const categories = [
    {
      id: 'drills',
      icon: Flame,
      label: 'Drills',
      percentage: drills,
      status: drills >= 90
        ? '✓ All compliant'
        : overdueDrillsCount > 0
          ? `⚠️ ${overdueDrillsCount} overdue`
          : '⚠️ Attention needed',
      statusColor: drills >= 90 ? 'text-green-600' : 'text-red-600',
    },
    {
      id: 'equipment',
      icon: Wrench,
      label: 'Maintenance',
      percentage: equipment,
      status: equipment >= 90
        ? '✓ All compliant'
        : overdueJobsCount > 0
          ? `✓ ${overdueJobsCount} due soon`
          : '✓ On track',
      statusColor: equipment >= 90 ? 'text-green-600' : 'text-amber-600',
    },
    {
      id: 'crew',
      icon: Users,
      label: 'Crew',
      percentage: crew,
      status: crew >= 90
        ? '✓ All compliant'
        : expiringCertsCount > 0
          ? `⚠️ ${expiringCertsCount} certs expiring`
          : '⚠️ Attention needed',
      statusColor: crew >= 90 ? 'text-green-600' : 'text-red-600',
    },
    {
      id: 'defects',
      icon: CircleCheckBig,
      label: 'Defects',
      percentage: defects,
      status: defects === 100
        ? '✓ None open'
        : openDefectsCount > 0
          ? `⚠️ ${openDefectsCount} open`
          : '⚠️ Open defects',
      statusColor: defects === 100 ? 'text-green-600' : 'text-red-600',
    },
  ];

  return (
    <div className="bg-white rounded-lg ring-1 ring-gray-100 shadow-md p-6">
      {/* Header - FT-185: Updated to "Compliance Overview" */}
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        Compliance Overview
      </h3>

      {/* FT-185: Category Breakdown with color-coded progress bars */}
      <div className="space-y-3">
        {categories.map((category) => {
          const Icon = category.icon;
          return (
            <div key={category.id} className="relative">
              <button
                onClick={() => onCategoryClick && onCategoryClick(category.id)}
                className="w-full p-3 rounded-lg hover:bg-slate-50 transition-colors group"
              >
                {/* FT-185: New layout - Icon + Label on left, Progress bar in middle, Percentage on right */}
                <div className="flex items-center gap-3">
                  {/* Icon */}
                  <Icon className="h-5 w-5 text-cyan-600 flex-shrink-0" />

                  {/* Label */}
                  <span className="text-sm font-medium text-slate-900 w-24 text-left">
                    {category.label}
                  </span>

                  {/* FT-185: Color-coded progress bar */}
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${getProgressBarColor(category.percentage)}`}
                      style={{ width: `${category.percentage}%` }}
                    />
                  </div>

                  {/* Percentage */}
                  <span className="text-sm font-semibold text-slate-700 w-12 text-right">
                    {category.percentage}%
                  </span>

                  {/* FT-185 follow-up: Traffic light dot - green 80-100%, amber 50-79%, red <50% */}
                  <div
                    className={`w-3 h-3 rounded-full flex-shrink-0 ${getStatusDotColor(category.percentage)}`}
                    aria-label={`Status: ${category.percentage >= 80 ? 'Good' : category.percentage >= 50 ? 'Warning' : 'Critical'}`}
                  />
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Compliance Card Component (OLD - kept for reference)
const ComplianceCard = ({ label, percentage, icon: Icon, onClick }) => {
  const getStatusColor = (pct) => {
    if (pct >= 90) return { bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-200' };
    if (pct >= 70) return { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' };
    return { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200' };
  };

  const colors = getStatusColor(percentage);

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 rounded-lg ${colors.bg} ring-1 ${colors.ring} hover:shadow-md transition-all text-left`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-cyan-600" />
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>
        <span className={`text-2xl font-bold ${colors.text}`}>{percentage}%</span>
      </div>
    </button>
  );
};

// FT-185: ComplianceHero Component - DEPRECATED (use CompactComplianceCard instead)
// This component is kept for backwards compatibility but no longer used in production
const ComplianceHero = ({ compliance, onCategoryClick }) => {
  // FT-185 follow-up: Progress bar color - cyan for 50%+, red only below 50%
  const getProgressBarColor = (pct) => {
    if (pct >= 50) return 'bg-cyan-600';
    return 'bg-red-500';
  };

  // FT-185 follow-up: Traffic light dot color
  const getStatusDotColor = (pct) => {
    if (pct >= 80) return 'bg-green-500';
    if (pct >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const categories = [
    { id: 'drills', label: 'Drills', percentage: compliance.drills, icon: Flame },
    { id: 'equipment', label: 'Maintenance', percentage: compliance.equipment, icon: Wrench },
    { id: 'crew', label: 'Crew', percentage: compliance.crew, icon: Users },
    { id: 'defects', label: 'Defects', percentage: compliance.defects, icon: CircleCheckBig },
  ];

  return (
    <div className="bg-white rounded-lg ring-1 ring-gray-100 shadow-md p-6">
      {/* FT-185: Header */}
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        Compliance Overview
      </h3>

      {/* FT-185: Category list with progress bars */}
      <div className="space-y-3">
        {categories.map((category) => {
          const Icon = category.icon;
          return (
            <button
              key={category.id}
              onClick={() => onCategoryClick?.(category.id)}
              className="w-full p-3 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Icon className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-900 w-24 text-left">
                  {category.label}
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${getProgressBarColor(category.percentage)}`}
                    style={{ width: `${category.percentage}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-slate-700 w-12 text-right">
                  {category.percentage}%
                </span>
                {/* FT-185 follow-up: Traffic light dot */}
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${getStatusDotColor(category.percentage)}`}
                  aria-label={`Status: ${category.percentage >= 80 ? 'Good' : category.percentage >= 50 ? 'Warning' : 'Critical'}`}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Primary CTA Component
const PrimaryCTA = ({ actionText, statusText, statusType, onClick }) => {
  const statusColors = {
    overdue: { bg: 'bg-red-100', text: 'text-red-800' },
    due: { bg: 'bg-amber-100', text: 'text-amber-800' },
    ok: { bg: 'bg-green-100', text: 'text-green-800' }
  };

  const colors = statusColors[statusType] || statusColors.overdue;

  return (
    <button
      onClick={onClick}
      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg p-6 shadow-md transition-all group"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Flame className="h-8 w-8" />
          <div className="text-left">
            <span className="text-lg font-medium block">{actionText}</span>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold ${colors.bg} ${colors.text}`}>
              {statusText}
            </span>
          </div>
        </div>
        <ChevronRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
      </div>
    </button>
  );
};

// Attention Item Component
const AttentionItem = ({ icon: Icon, title, daysOverdue, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-all group"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-cyan-600" />
        <span className="font-medium text-gray-900">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-red-600 font-bold text-sm">{daysOverdue} days overdue</span>
        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all" />
      </div>
    </button>
  );
};

// Attention Required Component
const AttentionRequired = ({ items }) => {
  return (
    <div className="bg-white rounded-lg ring-1 ring-gray-100 shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Attention Required
      </h3>
      <div className="space-y-3">
        {items.map((item) => (
          <AttentionItem
            key={item.id}
            icon={item.icon}
            title={item.title}
            daysOverdue={item.daysOverdue}
            onClick={item.onClick}
          />
        ))}
      </div>
    </div>
  );
};

// Mini-Card Component for Category Cards - Uses correct images per module type
const MiniCard = ({ item, onNavigate }) => {
  const Icon = item.icon; // Lucide icon component

  // Determine rendering type based on module
  const useMaintenanceImage = item.module === 'maintenance'; // Maintenance: category images
  const useDrillImage = item.module === 'drills'; // Drills: drill-type images
  const useProfilePic = item.module === 'crew' && item.profilePic; // Crew: profile pics if available
  const useIcon = !useMaintenanceImage && !useDrillImage && !useProfilePic; // Default: icon (crew without pic, defects)

  // Get maintenance category image path
  const getMaintenanceImage = (category) => {
    const categoryLower = (category || 'default').toLowerCase();
    const validCategories = ['ffe', 'lsa', 'navigation', 'machinery', 'environmental', 'electrical', 'medical', 'operations', 'gmdss'];

    if (validCategories.includes(categoryLower)) {
      return `/images/maintenance/categories/${categoryLower}.jpg`;
    }
    return `/images/maintenance/categories/default.jpg`;
  };

  // Get drill image path (same logic as DrillCardEnhanced)
  const getDrillImage = () => {
    const drillType = (item.drillType || '').toLowerCase();

    if (drillType.includes('fire')) {
      return '/images/drills/fire-drill.jpg';
    }
    if (drillType.includes('abandon') || drillType.includes('ship')) {
      return '/images/drills/abandon-ship.jpg';
    }
    if (drillType.includes('man_overboard') || drillType.includes('mob') ||
        (drillType.includes('man') && drillType.includes('overboard'))) {
      return '/images/drills/man-overboard.jpg';
    }

    // Default drill category image
    return '/images/drills/drill-category.jpg';
  };

  // Status styling
  // FT-92B: Support both underscore (from hooks) and hyphen formats
  const statusStyles = {
    'overdue': 'bg-red-50 border-red-200 hover:bg-red-100',
    'due-soon': 'bg-amber-50 border-amber-200 hover:bg-amber-100',
    'due_soon': 'bg-amber-50 border-amber-200 hover:bg-amber-100',
    'compliant': 'bg-white border-gray-200 hover:bg-gray-50',
    'ok': 'bg-white border-gray-200 hover:bg-gray-50'
  };

  const indicatorStyles = {
    'overdue': 'bg-red-500',
    'due-soon': 'bg-amber-500',
    'due_soon': 'bg-amber-500',
    'compliant': 'bg-green-500',
    'ok': 'bg-green-500'
  };

  return (
    <button
      onClick={() => onNavigate(item.module)}
      className={`
        w-full flex items-start gap-3 p-3 rounded-lg border
        transition-colors cursor-pointer min-h-[44px]
        ${statusStyles[item.status]}
      `}
    >
      {/* Left: Image OR Icon based on module type */}
      <div className="flex-shrink-0">
        {useMaintenanceImage && (
          // Maintenance: Category images (FFE, LSA, etc.)
          <img
            src={getMaintenanceImage(item.category)}
            alt={item.category || 'Item'}
            className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover shadow-sm"
            onError={(e) => {
              e.target.src = '/images/maintenance/categories/default.jpg';
            }}
          />
        )}

        {useDrillImage && (
          // Drills: Drill-type images (fire, MOB, abandon ship)
          <img
            src={getDrillImage()}
            alt={item.title}
            className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover shadow-sm"
            onError={(e) => {
              // Fallback to icon container if drill image fails
              e.target.style.display = 'none';
              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
            }}
          />
        )}
        {useDrillImage && (
          // Drill icon fallback (hidden unless image fails)
          <div
            className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-slate-100 items-center justify-center"
            style={{ display: 'none' }}
          >
            {Icon && <Icon className="h-6 w-6 md:h-7 md:w-7 text-slate-600" />}
          </div>
        )}

        {useProfilePic && (
          // Crew with profile pic: Show circular photo
          <>
            <img
              src={item.profilePic}
              alt={item.title}
              className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover shadow-sm"
              onError={(e) => {
                // Fallback to icon if profile pic fails
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div
              className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-slate-100 items-center justify-center"
              style={{ display: 'none' }}
            >
              {Icon && <Icon className="h-6 w-6 md:h-7 md:w-7 text-slate-600" />}
            </div>
          </>
        )}

        {useIcon && (
          // Default: Use lucide icon (crew without pic, defects) - teal brand color, no box
          <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center">
            {Icon && <Icon className="h-6 w-6 md:h-7 md:w-7 text-cyan-600" />}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 text-left min-w-0 py-0.5">
        <div className="font-medium text-gray-900 text-sm truncate">
          {item.title}
        </div>
        <div className="text-xs text-gray-600 mt-0.5 truncate">
          {item.subtitle}
        </div>
      </div>

      {/* Status Indicator - Far right */}
      <div className="flex-shrink-0 flex items-center gap-2 py-0.5">
        {item.isOverdue && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            Overdue
          </span>
        )}
        {item.isDueSoon && !item.isOverdue && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
            Due Soon
          </span>
        )}
        <div className={`h-2.5 w-2.5 rounded-full ${indicatorStyles[item.status] || 'bg-gray-300'}`} />
      </div>
    </button>
  );
};

// Category Card Component
const CategoryCard = ({ category, onNavigate }) => {
  if (!category) {
    return null;
  }

  const Icon = category.icon;
  const hasItems = category.items && category.items.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Card Header - Dark Slate */}
      <div className="bg-slate-700 px-5 py-4 flex items-center justify-between">
        {/* Icon + Title - Left side */}
        <div className="flex items-center gap-3">
          {/* Icon WITHOUT circle - bigger size */}
          <Icon className="h-7 w-7 text-white" />

          {/* Title */}
          <div>
            <h3 className="text-base font-semibold text-white">
              {category.title}
            </h3>
            <p className="text-xs text-slate-300 mt-0.5">
              {hasItems ? `${category.items.length} upcoming` : 'All up to date'}
            </p>
          </div>
        </div>

        {/* Arrow - Right side */}
        <button
          onClick={() => onNavigate(category.module)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors group"
          aria-label={`View all ${category.title}`}
        >
          <svg
            className="h-5 w-5 text-slate-400 group-hover:text-white transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Card Body - White with scroll */}
      <div className="relative">
        {/* Card Body - Desktop: scroll inside card, Mobile: no scroll (page scrolls) */}
        <div
          className="p-4 md:overflow-y-auto md:max-h-[300px]"
          style={{
            minHeight: '240px', // Minimum height for empty state
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgb(203 213 225) transparent'
          }}
        >
          {hasItems ? (
            <div className="space-y-2">
              {category.items.map(item => (
                <MiniCard key={item.id} item={item} onNavigate={onNavigate} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-900 mb-1">No upcoming items</p>
              <p className="text-xs text-gray-500">All {category.title.toLowerCase()} are up to date</p>
            </div>
          )}
        </div>

        {/* Scroll indicator gradient (only show if scrollable) */}
        {hasItems && category.items.length > 3 && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
};

// Category Cards Grid Component
const CategoryCardsGrid = ({ categories, onNavigate }) => {
  if (!categories || categories.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <CircleCheckBig className="h-12 w-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          All Up to Date
        </h3>
        <p className="text-sm text-gray-600">
          No upcoming items in the next 30 days. Great work!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        Upcoming by Category
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map(category => (
          <CategoryCard
            key={category.id}
            category={category}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
};

// Mobile Footer Menu Component
const DashboardMobileFooter = ({ onRecordDrill, onReportDefect, onManageCrew, onVoyage }) => {
  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] z-10">
      <div className="flex items-center justify-around gap-2">
        {/* Record Drill */}
        <button
          onClick={onRecordDrill}
          className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg hover:bg-cyan-50 transition-colors flex-1"
        >
          <Flame className="h-5 w-5 text-cyan-600" />
          <span className="text-xs font-medium text-gray-700">Drill</span>
        </button>

        {/* Report Defect */}
        <button
          onClick={onReportDefect}
          className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg hover:bg-cyan-50 transition-colors flex-1"
        >
          <Wrench className="h-5 w-5 text-cyan-600" />
          <span className="text-xs font-medium text-gray-700">Defect</span>
        </button>

        {/* Manage Crew */}
        <button
          onClick={onManageCrew}
          className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg hover:bg-cyan-50 transition-colors flex-1"
        >
          <Users className="h-5 w-5 text-cyan-600" />
          <span className="text-xs font-medium text-gray-700">Crew</span>
        </button>

        {/* Voyage */}
        <button
          onClick={onVoyage}
          className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg hover:bg-cyan-50 transition-colors flex-1"
        >
          <Ship className="h-5 w-5 text-cyan-600" />
          <span className="text-xs font-medium text-gray-700">Voyage</span>
        </button>
      </div>
    </div>
  );
};

// Mobile Vessel Status Bar - shows at top on mobile only
const MobileVesselStatusBar = ({ onNavigate }) => {
  const { activeVoyage, voyages } = useVoyages();

  // Determine status
  const isUnderway = activeVoyage && activeVoyage.status === 'underway';

  // Calculate POB
  const totalPOB = activeVoyage
    ? (activeVoyage.crewCount || 0) + (activeVoyage.passengerCount || 0)
    : 0;

  // Get fuel level and color
  const fuelLevel = activeVoyage?.currentFuel ?? null;
  const getFuelColorClass = (level) => {
    if (level >= 75) return 'text-green-600';
    if (level >= 50) return 'text-cyan-600';
    if (level >= 25) return 'text-amber-600';
    return 'text-red-600';
  };

  // Get last completed voyage for location when alongside
  const lastVoyage = useMemo(() => {
    if (activeVoyage) return null;
    return voyages
      .filter(v => v.status === 'completed')
      .sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime())[0];
  }, [voyages, activeVoyage]);

  const currentLocation = lastVoyage?.to?.name;

  return (
    <div
      className="xl:hidden mb-4"
      onClick={() => onNavigate('operations')}
    >
      <div className="bg-white rounded-lg ring-1 ring-gray-100 shadow-sm px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors">
        {/* Left: Status */}
        <div className="flex items-center gap-3">
          {isUnderway ? (
            <>
              <Ship className="h-5 w-5 text-green-600" />
              <span className="font-medium text-slate-800">Underway</span>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </>
          ) : (
            <>
              <Anchor className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-slate-800">Alongside</span>
              {currentLocation && (
                <span className="text-sm text-slate-500 truncate max-w-[120px]">
                  @ {currentLocation}
                </span>
              )}
            </>
          )}
        </div>

        {/* Center: Stats (only when underway) */}
        {isUnderway && (
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-600">
              <Users className="h-4 w-4 inline mr-1" />
              {totalPOB} POB
            </span>
            {fuelLevel !== null && (
              <span className={getFuelColorClass(fuelLevel)}>
                <Fuel className="h-4 w-4 inline mr-1" />
                {fuelLevel}%
              </span>
            )}
          </div>
        )}

        {/* Right: Action Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate('operations');
          }}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 ${
            isUnderway
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
          } transition-colors`}
        >
          {isUnderway ? 'View' : 'Start Voyage'}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Day Details List Component
const DayDetailsList = ({ date, onNavigate }) => {
  const { getEventsForDate } = useUpcomingEvents();
  const events = getEventsForDate(new Date(date));
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const isToday = toLocalDateString(d) === toLocalDateString(today);
    if (isToday) return 'TODAY';
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  if (events.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        No items scheduled
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 pt-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">
        {formatDate(date)}
      </h4>
      <div className="space-y-2">
        {events.map((event) => {
          const Icon = event.icon;
          return (
            <button
              key={event.id}
              onClick={() => onNavigate(event.module)}
              className="w-full flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <Icon className={`h-5 w-5 ${event.iconColor} flex-shrink-0 mt-0.5`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {event.title}
                </p>
                {event.isOverdue && (
                  <p className="text-xs text-red-600 font-medium">
                    OVERDUE {event.daysOverdue} days
                  </p>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Week Summary Component
const WeekSummary = ({ weekStartDate }) => {
  const { getEventsForWeek } = useUpcomingEvents();
  const weekEvents = getEventsForWeek(weekStartDate);
  const totalCount = weekEvents.reduce((sum, day) => sum + day.events.length, 0);

  if (totalCount === 0) {
    return (
      <div className="text-center py-6">
        <CircleCheckBig className="h-10 w-10 text-green-500 mx-auto mb-2" />
        <p className="text-sm text-gray-600">No upcoming items this week</p>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 pt-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
        THIS WEEK ({totalCount} items)
      </p>
      <div className="space-y-3">
        {weekEvents.slice(0, 4).map((day) => {
          const dayDate = new Date(day.dateString);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isToday = toLocalDateString(dayDate) === toLocalDateString(today);

          return (
            <div key={day.dateString} className="text-sm">
              <p className="font-medium text-gray-700 mb-1">
                {isToday ? 'TODAY' : dayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </p>
              <div className="pl-3 space-y-1">
                {day.events.slice(0, 2).map(event => {
                  const Icon = event.icon;
                  return (
                    <div key={event.id} className="flex items-center gap-2 text-xs text-gray-600">
                      <Icon className={`h-3.5 w-3.5 ${event.iconColor}`} />
                      <span className="truncate">{event.title}</span>
                      {event.isOverdue && (
                        <span className="text-red-600 font-medium whitespace-nowrap">(OVERDUE {event.daysOverdue}d)</span>
                      )}
                    </div>
                  );
                })}
                {day.events.length > 2 && (
                  <p className="text-xs text-gray-500 pl-5">
                    +{day.events.length - 2} more
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Calendar Week View Component
const CalendarWeekView = ({ weekStartDate, onWeekChange, onDayClick, expandedDate, onNavigate }) => {
  const { getEventsForDate, getIconCountsForDate } = useUpcomingEvents();

  // Generate 7 days starting from weekStartDate
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStartDate);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  }, [weekStartDate]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3);
  };

  const formatDayNumber = (date) => {
    return date.getDate();
  };

  const isToday = (date) => {
    return toLocalDateString(date) === toLocalDateString(today);
  };

  // Get week label
  const getWeekLabel = () => {
    const weekEnd = new Date(weekStartDate);
    weekEnd.setDate(weekEnd.getDate() + 6);

    // Check if this is current week
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const dayOfWeek = todayStart.getDay();
    const currentWeekStart = new Date(todayStart);
    currentWeekStart.setDate(currentWeekStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    if (toLocalDateString(weekStartDate) === toLocalDateString(currentWeekStart)) {
      return 'This Week';
    }

    // Format as "Dec 16 - 22"
    const startMonth = weekStartDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = weekStartDate.getDate();
    const endDay = weekEnd.getDate();

    if (weekStartDate.getMonth() === weekEnd.getMonth()) {
      return `${startMonth} ${startDay} - ${endDay}`;
    } else {
      const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }
  };

  return (
    <div className="bg-white rounded-lg ring-1 ring-gray-100 shadow-md p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Upcoming
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onWeekChange(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600" />
          </button>
          <span className="text-sm font-medium text-gray-600 min-w-[100px] text-center">
            {getWeekLabel()}
          </span>
          <button
            onClick={() => onWeekChange(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {weekDays.map((day, index) => {
          const counts = getIconCountsForDate(day);
          const isTodayDate = isToday(day);
          const dateString = toLocalDateString(day);
          const isExpanded = expandedDate === dateString;

          return (
            <button
              key={index}
              onClick={() => onDayClick(dateString)}
              className={`
                flex flex-col items-center p-2 rounded-lg transition-all duration-200 ease-in-out min-h-[70px]
                cursor-pointer hover:shadow-md
                ${isExpanded
                  ? 'bg-cyan-100 ring-2 ring-cyan-500 shadow-lg'
                  : isTodayDate
                    ? 'bg-cyan-50 ring-2 ring-cyan-400'
                    : counts.priority === 'red'
                      ? 'bg-red-50 ring-1 ring-red-300'
                      : counts.priority === 'amber'
                        ? 'bg-amber-50 ring-1 ring-amber-300'
                        : 'bg-white hover:bg-gray-50'
                }
              `}
            >
              {/* Day name */}
              <span className="text-xs text-gray-500 font-medium">
                {formatDayName(day)}
              </span>

              {/* Day number */}
              <span className={`text-lg font-bold ${isTodayDate ? 'text-cyan-600' : 'text-gray-900'}`}>
                {formatDayNumber(day)}
              </span>

              {/* Icons */}
              {counts.total > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-0.5 mt-1">
                  {counts.drill > 0 && (
                    <div className="flex items-center">
                      <Flame className="h-3 w-3 text-cyan-600" />
                      {counts.drill > 1 && <span className="text-[10px] text-gray-600">{counts.drill}</span>}
                    </div>
                  )}
                  {counts.equipment > 0 && (
                    <div className="flex items-center">
                      <Wrench className="h-3 w-3 text-cyan-600" />
                      {counts.equipment > 1 && <span className="text-[10px] text-gray-600">{counts.equipment}</span>}
                    </div>
                  )}
                  {counts.certificate > 0 && (
                    <div className="flex items-center">
                      <Users className="h-3 w-3 text-cyan-600" />
                      {counts.certificate > 1 && <span className="text-[10px] text-gray-600">{counts.certificate}</span>}
                    </div>
                  )}
                  {counts.defect > 0 && (
                    <div className="flex items-center">
                      <AlertCircle className="h-3 w-3 text-cyan-600" />
                      {counts.defect > 1 && <span className="text-[10px] text-gray-600">{counts.defect}</span>}
                    </div>
                  )}
                  {counts.risk_assessment > 0 && (
                    <div className="flex items-center">
                      <ShieldAlert className="h-3 w-3 text-cyan-600" />
                      {counts.risk_assessment > 1 && <span className="text-[10px] text-gray-600">{counts.risk_assessment}</span>}
                    </div>
                  )}
                  {counts.familiarisation > 0 && (
                    <div className="flex items-center">
                      <GraduationCap className="h-3 w-3 text-cyan-600" />
                      {counts.familiarisation > 1 && <span className="text-[10px] text-gray-600">{counts.familiarisation}</span>}
                    </div>
                  )}
                  {counts.compliance > 0 && (
                    <div className="flex items-center">
                      <FileCheck className="h-3 w-3 text-emerald-600" />
                      {counts.compliance > 1 && <span className="text-[10px] text-gray-600">{counts.compliance}</span>}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Expanded Day Details */}
      {expandedDate && (
        <DayDetailsList
          date={expandedDate}
          onNavigate={onNavigate}
        />
      )}

      {/* This Week Summary (if no day expanded) */}
      {!expandedDate && (
        <WeekSummary weekStartDate={weekStartDate} />
      )}
    </div>
  );
};

// Calendar Month View Component
const CalendarMonthView = ({ currentMonth, onMonthChange, onDateClick }) => {
  const { getIconCountsForDate } = useUpcomingEvents();

  // Generate calendar grid for month
  const monthGrid = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday

    // Last day of month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Build grid (6 weeks max)
    const grid = [];
    let currentDate = 1;

    for (let week = 0; week < 6; week++) {
      const weekDays = [];

      for (let day = 0; day < 7; day++) {
        const cellIndex = week * 7 + day;

        // Empty cells before month starts
        if (cellIndex < firstDayOfWeek) {
          weekDays.push(null);
        }
        // Days in month
        else if (currentDate <= daysInMonth) {
          const date = new Date(year, month, currentDate);
          weekDays.push(date);
          currentDate++;
        }
        // Empty cells after month ends
        else {
          weekDays.push(null);
        }
      }

      grid.push(weekDays);

      // Stop if month is complete
      if (currentDate > daysInMonth) break;
    }

    return grid;
  }, [currentMonth]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = (date) => {
    if (!date) return false;
    return toLocalDateString(date) === toLocalDateString(today);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <button
          onClick={() => onMonthChange(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>

        <h3 className="text-lg font-semibold text-gray-900 min-w-[160px] text-center">
          {formatMonthYear(currentMonth)}
        </h3>

        <button
          onClick={() => onMonthChange(1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Day names header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="space-y-1">
        {monthGrid.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-1">
            {week.map((date, dayIndex) => {
              if (!date) {
                // Empty cell
                return <div key={dayIndex} className="aspect-square" />;
              }

              const counts = getIconCountsForDate(date);
              const isTodayDate = isToday(date);
              const dateString = toLocalDateString(date);

              return (
                <button
                  key={dayIndex}
                  onClick={() => onDateClick(dateString, date)}
                  className={`
                    aspect-square flex flex-col items-center justify-center p-1 rounded-lg
                    transition-all duration-200 ease-in-out text-center min-h-[44px]
                    cursor-pointer hover:shadow-md
                    ${isTodayDate
                      ? 'bg-cyan-50 ring-2 ring-cyan-400'
                      : counts.priority === 'red'
                        ? 'bg-red-50 ring-1 ring-red-300'
                        : counts.priority === 'amber'
                          ? 'bg-amber-50 ring-1 ring-amber-300'
                          : 'bg-white hover:bg-gray-50'
                    }
                  `}
                >
                  {/* Day number */}
                  <span className={`text-sm font-medium mb-0.5 ${isTodayDate ? 'text-cyan-600' : 'text-gray-900'}`}>
                    {date.getDate()}
                  </span>

                  {/* Icons (mobile: stack, desktop: row) */}
                  {counts.total > 0 && (
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-0.5">
                      {counts.drill > 0 && (
                        <Flame className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-cyan-600" />
                      )}
                      {counts.equipment > 0 && (
                        <Wrench className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-cyan-600" />
                      )}
                      {counts.certificate > 0 && (
                        <Users className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-cyan-600" />
                      )}
                      {counts.defect > 0 && (
                        <AlertCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-cyan-600" />
                      )}
                      {counts.risk_assessment > 0 && (
                        <ShieldAlert className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-cyan-600" />
                      )}
                      {counts.familiarisation > 0 && (
                        <GraduationCap className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-cyan-600" />
                      )}
                      {counts.compliance > 0 && (
                        <FileCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-emerald-600" />
                      )}
                    </div>
                  )}

                  {/* Overdue indicator */}
                  {counts.hasOverdue && (
                    <div className="flex gap-0.5 mt-0.5">
                      <div className="w-1 h-1 rounded-full bg-red-600" />
                      <div className="w-1 h-1 rounded-full bg-red-600" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <Flame className="h-3.5 w-3.5 text-cyan-600" />
            <span>Drills</span>
          </div>
          <div className="flex items-center gap-1">
            <Wrench className="h-3.5 w-3.5 text-cyan-600" />
            <span>Equipment</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5 text-cyan-600" />
            <span>Certificates</span>
          </div>
          <div className="flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5 text-cyan-600" />
            <span>Defects</span>
          </div>
          <div className="flex items-center gap-1">
            <ShieldAlert className="h-3.5 w-3.5 text-cyan-600" />
            <span>Risk</span>
          </div>
          <div className="flex items-center gap-1">
            <GraduationCap className="h-3.5 w-3.5 text-cyan-600" />
            <span>Familiarisation</span>
          </div>
          <div className="flex items-center gap-1">
            <FileCheck className="h-3.5 w-3.5 text-emerald-600" />
            <span>Compliance</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="flex gap-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-red-600" />
              <div className="w-1.5 h-1.5 rounded-full bg-red-600" />
            </div>
            <span>Overdue</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Week Grid Inline Component (for desktop week view without wrapper)
const WeekGridInline = ({ weekStartDate, onDayClick, expandedDate, onNavigate }) => {
  const { getIconCountsForDate } = useUpcomingEvents();

  // Generate 7 days starting from weekStartDate
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStartDate);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  }, [weekStartDate]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3);
  };

  const formatDayNumber = (date) => {
    return date.getDate();
  };

  const isToday = (date) => {
    return toLocalDateString(date) === toLocalDateString(today);
  };

  return (
    <>
      {/* Week Grid */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {weekDays.map((day, index) => {
          const counts = getIconCountsForDate(day);
          const isTodayDate = isToday(day);
          const dateString = toLocalDateString(day);
          const isExpanded = expandedDate === dateString;

          return (
            <button
              key={index}
              onClick={() => onDayClick(dateString)}
              className={`
                flex flex-col items-center p-2 rounded-lg transition-all duration-200 ease-in-out min-h-[70px]
                cursor-pointer hover:shadow-md
                ${isExpanded
                  ? 'bg-cyan-100 ring-2 ring-cyan-500 shadow-lg'
                  : isTodayDate
                    ? 'bg-cyan-50 ring-2 ring-cyan-400'
                    : counts.priority === 'red'
                      ? 'bg-red-50 ring-1 ring-red-300'
                      : counts.priority === 'amber'
                        ? 'bg-amber-50 ring-1 ring-amber-300'
                        : 'bg-white hover:bg-gray-50'
                }
              `}
            >
              {/* Day name */}
              <span className="text-xs text-gray-500 font-medium">
                {formatDayName(day)}
              </span>

              {/* Day number */}
              <span className={`text-lg font-bold ${isTodayDate ? 'text-cyan-600' : 'text-gray-900'}`}>
                {formatDayNumber(day)}
              </span>

              {/* Icons */}
              {counts.total > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-0.5 mt-1">
                  {counts.drill > 0 && (
                    <div className="flex items-center">
                      <Flame className="h-3 w-3 text-cyan-600" />
                      {counts.drill > 1 && <span className="text-[10px] text-gray-600">{counts.drill}</span>}
                    </div>
                  )}
                  {counts.equipment > 0 && (
                    <div className="flex items-center">
                      <Wrench className="h-3 w-3 text-cyan-600" />
                      {counts.equipment > 1 && <span className="text-[10px] text-gray-600">{counts.equipment}</span>}
                    </div>
                  )}
                  {counts.certificate > 0 && (
                    <div className="flex items-center">
                      <Users className="h-3 w-3 text-cyan-600" />
                      {counts.certificate > 1 && <span className="text-[10px] text-gray-600">{counts.certificate}</span>}
                    </div>
                  )}
                  {counts.defect > 0 && (
                    <div className="flex items-center">
                      <AlertCircle className="h-3 w-3 text-cyan-600" />
                      {counts.defect > 1 && <span className="text-[10px] text-gray-600">{counts.defect}</span>}
                    </div>
                  )}
                  {counts.risk_assessment > 0 && (
                    <div className="flex items-center">
                      <ShieldAlert className="h-3 w-3 text-cyan-600" />
                      {counts.risk_assessment > 1 && <span className="text-[10px] text-gray-600">{counts.risk_assessment}</span>}
                    </div>
                  )}
                  {counts.familiarisation > 0 && (
                    <div className="flex items-center">
                      <GraduationCap className="h-3 w-3 text-cyan-600" />
                      {counts.familiarisation > 1 && <span className="text-[10px] text-gray-600">{counts.familiarisation}</span>}
                    </div>
                  )}
                  {counts.compliance > 0 && (
                    <div className="flex items-center">
                      <FileCheck className="h-3 w-3 text-emerald-600" />
                      {counts.compliance > 1 && <span className="text-[10px] text-gray-600">{counts.compliance}</span>}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Expanded Day Details */}
      {expandedDate && (
        <DayDetailsList
          date={expandedDate}
          onNavigate={onNavigate}
        />
      )}

      {/* This Week Summary (if no day expanded) */}
      {!expandedDate && (
        <WeekSummary weekStartDate={weekStartDate} />
      )}
    </>
  );
};

// Day Details Modal Component
const DayDetailsModal = ({ date, onClose, onNavigate }) => {
  const { getEventsForDate } = useUpcomingEvents();

  if (!date) return null;

  const dateObj = new Date(date);
  const events = getEventsForDate(dateObj);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const isToday = toLocalDateString(d) === toLocalDateString(today);
    if (isToday) return 'TODAY';
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {formatDate(date)}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(80vh-140px)]">
            {events.length === 0 ? (
              <div className="text-center py-8">
                <CalendarDays className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No items scheduled</p>
                <p className="text-sm text-gray-500 mt-1">You're all clear for this day</p>
              </div>
            ) : (
              <div className="space-y-3">
                {events.map((event) => {
                  const Icon = event.icon;
                  return (
                    <button
                      key={event.id}
                      onClick={() => {
                        onClose();
                        onNavigate(event.module);
                      }}
                      className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left border border-gray-200"
                    >
                      <Icon className={`h-5 w-5 ${event.iconColor} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {event.title}
                        </p>
                        {event.isOverdue && (
                          <p className="text-xs text-red-600 font-medium mt-1">
                            OVERDUE {event.daysOverdue} days
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================

/**
 * FT-324 cycle-3b: pure redirect helper — extracted so it can be unit-tested
 * without mounting the entire TailwindDashboard tree.
 *
 * When the user clicks the Dashboard's Daily Checks tile we MUST NOT render
 * DailyChecksQuickAccess in the Dashboard tree (it consumes
 * ChecklistRecordsContext, which is only mounted inside OperationsModule and
 * would throw). Instead, navigate to Operations → Checklists.
 */
export const buildOpenDailyChecksHandler = ({ setActiveModule, setActiveModuleWithTab }) => () => {
  if (setActiveModuleWithTab) {
    setActiveModuleWithTab('operations', 'checklists');
  } else if (setActiveModule) {
    setActiveModule('operations');
  }
};

const TailwindDashboard = ({ setActiveModule, setActiveModuleWithTab, onOpenVesselSettings }) => {
  const { currentVessel } = useVessel();

  // FT-324 cycle-3b: navigate to Operations → Checklists instead of opening
  // DailyChecksQuickAccess inline (which crashes outside the Provider tree).
  const handleOpenDailyChecks = buildOpenDailyChecksHandler({
    setActiveModule,
    setActiveModuleWithTab
  });

  // Real compliance data from vessel
  const compliance = useDashboardCompliance() || {
    overall: 0,
    drills: 0,
    equipment: 0,
    crew: 0,
    defects: 0,
    overdueDrillsCount: 0,
    overdueJobsCount: 0,
    expiringCertsCount: 0,
    openDefectsCount: 0
  };

  // Get data for PrimaryCTA and AttentionRequired
  const drillsData = useDrillsData();
  const overdueDrills = drillsData?.getOverdueDrills?.() || [];
  const upcomingDrills = drillsData?.getUpcomingDrills?.() || [];
  const { register: equipmentRegister } = useEquipmentRegister();
  const { crew } = useCrew();
  const defectsData = useDefects();

  // Get category cards data
  const categories = useDashboardCategories();

  // Calendar state
  const [weekStartDate, setWeekStartDate] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday
    const monday = new Date(today);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  const [expandedDate, setExpandedDate] = useState(null);

  // Month view state
  const [calendarView, setCalendarView] = useState('week'); // 'week' or 'month'
  const [selectedModalDate, setSelectedModalDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  // Handle month navigation
  const handleMonthChange = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  // Handle date click (opens modal for month view)
  const handleDateClick = (dateString) => {
    setSelectedModalDate(dateString);
  };

  // Close modal
  const handleCloseModal = () => {
    setSelectedModalDate(null);
  };

  const handleWeekChange = (direction) => {
    setWeekStartDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (direction * 7));
      return newDate;
    });
    setExpandedDate(null); // Collapse when changing weeks
  };

  const handleDayClick = (dateString) => {
    setExpandedDate(expandedDate === dateString ? null : dateString);
  };

  // Navigation handler
  const handleNavigate = (module) => {
    setActiveModule(module);
  };

  // Handle category clicks
  const handleCategoryClick = (category) => {
    const routes = {
      drills: 'drills',          // Navigate to Drills module
      equipment: 'maintenance',  // Navigate to Equipment/Maintenance module
      crew: 'crew',              // Navigate to Crew module
      defects: 'defects'         // Navigate to Defects module
    };
    setActiveModule(routes[category]);
  };

  // Calculate PrimaryCTA content based on real drill data
  const getMostUrgentDrill = () => {
    // If there are overdue drills, show the most overdue one
    if (overdueDrills.length > 0) {
      const mostOverdue = overdueDrills[0]; // Already sorted by daysOverdue
      return {
        actionText: `Complete ${mostOverdue.name}`,
        statusText: `${overdueDrills.length} OVERDUE`,
        statusType: 'overdue',
        onClick: () => handleNavigate('drills')
      };
    }

    // If no overdue but have upcoming, show next due
    if (upcomingDrills.length > 0) {
      const nextDrill = upcomingDrills[0];
      const daysUntilDue = Math.ceil((new Date(nextDrill.nextDue) - new Date()) / (1000 * 60 * 60 * 24));

      if (daysUntilDue <= 7) {
        return {
          actionText: `Prepare ${nextDrill.name}`,
          statusText: `DUE IN ${daysUntilDue} DAYS`,
          statusType: 'due',
          onClick: () => handleNavigate('drills')
        };
      }
    }

    // Everything is up to date
    return {
      actionText: 'All Drills Up to Date',
      statusText: 'ON TRACK',
      statusType: 'ok',
      onClick: () => handleNavigate('drills')
    };
  };

  const primaryCTA = getMostUrgentDrill();

  // Aggregate all attention-required items from all modules
  const getAttentionItems = () => {
    const items = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // === ADD OVERDUE DRILLS ===
    overdueDrills.slice(0, 2).forEach(drill => {
      items.push({
        id: `drill-${drill.type}`,
        icon: Flame,
        title: drill.name,
        daysOverdue: drill.daysOverdue,
        onClick: () => handleNavigate('drills'),
        urgency: drill.daysOverdue
      });
    });

    // === ADD OVERDUE EQUIPMENT ===
    if (equipmentRegister && Array.isArray(equipmentRegister)) {
      const overdueEquipment = equipmentRegister
        .filter(item => {
          if (!item.nextServiceDue && !item.nextDue && !item.serviceDue) return false;
          const dueDate = new Date(item.nextServiceDue || item.nextDue || item.serviceDue);
          if (isNaN(dueDate.getTime())) return false;
          return dueDate < today;
        })
        .map(item => {
          const dueDate = new Date(item.nextServiceDue || item.nextDue || item.serviceDue);
          const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
          return { ...item, daysOverdue };
        })
        .sort((a, b) => b.daysOverdue - a.daysOverdue);

      overdueEquipment.slice(0, 2).forEach(item => {
        items.push({
          id: `equipment-${item.id}`,
          icon: Wrench,
          title: item.name || item.itemName || 'Equipment Service',
          daysOverdue: item.daysOverdue,
          onClick: () => handleNavigate('maintenance'),
          urgency: item.daysOverdue
        });
      });
    }

    // === ADD EXPIRING CERTIFICATES ===
    if (crew && Array.isArray(crew)) {
      const warningDays = 30;
      let expiringCertsCount = 0;
      let mostUrgentExpiry = null;

      crew.forEach(crewMember => {
        if (crewMember.certificates && Array.isArray(crewMember.certificates)) {
          crewMember.certificates.forEach(cert => {
            const expiryDateStr = cert.expiryDate || cert.expiry;
            if (expiryDateStr) {
              const expiryDate = new Date(expiryDateStr);
              if (!isNaN(expiryDate.getTime())) {
                const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

                if (daysUntilExpiry <= warningDays) {
                  expiringCertsCount++;

                  if (!mostUrgentExpiry || daysUntilExpiry < mostUrgentExpiry.daysUntilExpiry) {
                    mostUrgentExpiry = {
                      crewName: crewMember.name || 'Crew Member',
                      certName: cert.name || cert.certificateType || 'Certificate',
                      daysUntilExpiry
                    };
                  }
                }
              }
            }
          });
        }
      });

      if (expiringCertsCount > 0) {
        const daysOverdue = mostUrgentExpiry.daysUntilExpiry < 0
          ? Math.abs(mostUrgentExpiry.daysUntilExpiry)
          : 0;

        items.push({
          id: 'crew-certs',
          icon: Users,
          title: expiringCertsCount === 1
            ? `${mostUrgentExpiry.certName} Expiring`
            : `${expiringCertsCount} Certificates Expiring`,
          daysOverdue: daysOverdue || Math.abs(mostUrgentExpiry.daysUntilExpiry),
          onClick: () => handleNavigate('crew'),
          urgency: daysOverdue || (30 - mostUrgentExpiry.daysUntilExpiry)
        });
      }
    }

    // === ADD OPEN DEFECTS (if critical) ===
    const defectsList = defectsData?.defects || [];
    if (Array.isArray(defectsList) && defectsList.length > 0) {
      const criticalDefects = defectsList.filter(d => {
        const status = (d.status || '').toLowerCase();
        if (status === 'resolved' || status === 'closed') return false;

        const priority = (d.priority || '').toLowerCase();
        const severity = (d.severity || '').toLowerCase();
        const riskLevel = (d.risk_level || '').toLowerCase();

        return priority === 'critical' || priority === 'high' ||
               severity === 'critical' || severity === 'high' ||
               riskLevel === 'critical' || riskLevel === 'high';
      });

      criticalDefects.slice(0, 1).forEach(defect => {
        const reportedDate = new Date(defect.reportedDate || defect.createdAt || Date.now());
        const daysOpen = Math.floor((today - reportedDate) / (1000 * 60 * 60 * 24));

        items.push({
          id: `defect-${defect.id}`,
          icon: Wrench,
          title: defect.title || defect.description || 'Critical Defect',
          daysOverdue: daysOpen,
          onClick: () => handleNavigate('defects'),
          urgency: daysOpen + 100 // Critical defects get +100 urgency bonus
        });
      });
    }

    // Sort by urgency (most urgent first)
    items.sort((a, b) => b.urgency - a.urgency);

    // Return top 3 items
    return items.slice(0, 3);
  };

  const attentionItems = getAttentionItems();
  const hasAttentionItems = attentionItems.length > 0;

  return (
    <>
      <div className="">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Dashboard
          </h1>
          {/* FT-188: Show vessel name + welcome message */}
          <p className="mt-1 text-sm text-gray-500">
            {currentVessel?.name && (
              <>
                <span className="font-bold text-cyan-700">{currentVessel.name}</span>
                <span className="mx-1.5">·</span>
              </>
            )}
            Welcome back, {(() => {
              // FT-134: Try current_user (RBAC) first, then fallback to session
              const currentUser = JSON.parse(localStorage.getItem('current_user') || '{}');
              const session = JSON.parse(localStorage.getItem('sr.auth.session') || '{}');
              const fullName = currentUser?.name || session?.userName || '';
              return fullName.split(' ')[0] || 'Captain';
            })()}
          </p>
        </div>

        {/* NO TABS - Dashboard doesn't have tabs like Equipment/Drills */}

        {/* Mobile Vessel Status Bar - Quick access on mobile (only when vessel selected) */}
        {currentVessel && <MobileVesselStatusBar onNavigate={handleNavigate} />}

        {/* FT-80 Phase 6 + FT-324 cycle-3b: Daily Checks Status Card.
            Clicking it now navigates to Operations → Checklists tab (where the
            ChecklistRecordsProvider is mounted) instead of opening the modal
            inline in the Dashboard tree. Badge state is driven by the card's
            own useDailyChecks hook and remains unchanged. */}
        {currentVessel && <DashboardDailyChecksCard onOpenDailyChecks={handleOpenDailyChecks} />}

        {/* Main Content Grid */}
        <div className="mt-6 pb-20 md:pb-0">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">

            {/* LEFT COLUMN (2/3 width) */}
            <div className="xl:col-span-2 space-y-6">

              {/* Compact Compliance Card (v2) */}
              <CompactComplianceCard
                compliance={compliance}
                onCategoryClick={handleCategoryClick}
              />

              {/* Category Cards - Upcoming by Category */}
              <CategoryCardsGrid
                categories={categories}
                onNavigate={handleNavigate}
              />

              {/* Calendar Widget with View Toggle */}
              <div className="bg-white rounded-lg ring-1 ring-gray-100 shadow-md p-4">
                {/* Header with View Toggle */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Upcoming
                  </h3>

                  {/* View Toggle (visible on sm and up) */}
                  <div className="hidden sm:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setCalendarView('week')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        calendarView === 'week'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Week
                    </button>
                    <button
                      onClick={() => setCalendarView('month')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        calendarView === 'month'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Month
                    </button>
                  </div>
                </div>

                {/* Mobile: Month View Only */}
                <div className="sm:hidden">
                  <CalendarMonthView
                    currentMonth={currentMonth}
                    onMonthChange={handleMonthChange}
                    onDateClick={handleDateClick}
                  />
                </div>

                {/* Tablet/Desktop: Toggle between Week and Month */}
                <div className="hidden sm:block">
                  {calendarView === 'week' ? (
                    <div>
                      {/* Week navigation header */}
                      <div className="flex items-center justify-center gap-1 mb-4">
                        <button
                          onClick={() => handleWeekChange(-1)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          aria-label="Previous week"
                        >
                          <ChevronLeft className="h-4 w-4 text-gray-600" />
                        </button>
                        <span className="text-sm font-medium text-gray-600 min-w-[100px] text-center">
                          {(() => {
                            const weekEnd = new Date(weekStartDate);
                            weekEnd.setDate(weekEnd.getDate() + 6);
                            const todayStart = new Date();
                            todayStart.setHours(0, 0, 0, 0);
                            const dayOfWeek = todayStart.getDay();
                            const currentWeekStart = new Date(todayStart);
                            currentWeekStart.setDate(currentWeekStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
                            if (toLocalDateString(weekStartDate) === toLocalDateString(currentWeekStart)) {
                              return 'This Week';
                            }
                            const startMonth = weekStartDate.toLocaleDateString('en-US', { month: 'short' });
                            const startDay = weekStartDate.getDate();
                            const endDay = weekEnd.getDate();
                            if (weekStartDate.getMonth() === weekEnd.getMonth()) {
                              return `${startMonth} ${startDay} - ${endDay}`;
                            } else {
                              const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });
                              return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
                            }
                          })()}
                        </span>
                        <button
                          onClick={() => handleWeekChange(1)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          aria-label="Next week"
                        >
                          <ChevronRight className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                      {/* Inline Week Grid and Details */}
                      <WeekGridInline
                        weekStartDate={weekStartDate}
                        onDayClick={handleDayClick}
                        expandedDate={expandedDate}
                        onNavigate={handleNavigate}
                      />
                    </div>
                  ) : (
                    <CalendarMonthView
                      currentMonth={currentMonth}
                      onMonthChange={handleMonthChange}
                      onDateClick={handleDateClick}
                    />
                  )}
                </div>
              </div>

            </div>

            {/* RIGHT COLUMN (1/3 width) - Hidden on mobile (uses footer toolbar instead) */}
            <div className="hidden xl:block space-y-6">
              {/* Vessel Status Card - only when vessel selected */}
              {currentVessel && <VesselStatusCard onNavigate={handleNavigate} />}

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  {/* Record Drill - Navigate to Drills module */}
                  <button
                    onClick={() => handleNavigate('drills')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 transition-colors group"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center group-hover:bg-cyan-100 transition-colors">
                      <Flame className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-medium text-gray-900 block">Record Drill</span>
                      <span className="text-xs text-gray-500">Log completed safety drill</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                  </button>

                  {/* Report Defect - Navigate to Defects module */}
                  <button
                    onClick={() => handleNavigate('defects')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 transition-colors group"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center group-hover:bg-cyan-100 transition-colors">
                      <Wrench className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-medium text-gray-900 block">Report Defect</span>
                      <span className="text-xs text-gray-500">Log equipment issue</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                  </button>

                  {/* Manage Crew - Navigate to Crew module */}
                  <button
                    onClick={() => handleNavigate('crew')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 transition-colors group"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center group-hover:bg-cyan-100 transition-colors">
                      <Users className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-medium text-gray-900 block">Manage Crew</span>
                      <span className="text-xs text-gray-500">View crew & certificates</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Day Details Modal (for month view date clicks) */}
      {selectedModalDate && (
        <DayDetailsModal
          date={selectedModalDate}
          onClose={handleCloseModal}
          onNavigate={handleNavigate}
        />
      )}

      {/* FT-324 cycle-3b: Daily Checks modal removed from Dashboard.
          Clicking the tile now navigates to Operations → Checklists tab
          (see handleOpenDailyChecks + DashboardDailyChecksCard above).
          Rendering DailyChecksQuickAccess here crashes because it consumes
          ChecklistRecordsContext, which is only mounted inside OperationsModule. */}

      {/* Mobile Footer Menu */}
      <DashboardMobileFooter
        onRecordDrill={() => handleNavigate('drills')}
        onReportDefect={() => handleNavigate('defects')}
        onManageCrew={() => handleNavigate('crew')}
        onVoyage={() => handleNavigate('operations')}
      />
    </>
  );
};

export default TailwindDashboard;
